/**
 * AdminOrders — 销售订单看板 v3
 * - 自动生成 pp-YYYYMMDD-NNN 格式订单编号
 * - inquiry_id 正确关联线索表
 * - 支持一单多品 order_items 明细行
 * - 关联线索时从 quote_lines 完整还原所有明细
 * - order_items.total_amount 由数据库自动计算，前端不传
 */
import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  RefreshCw, Plus, Search, AlertCircle, ChevronDown, ChevronUp,
  FileText, Package2, CheckCircle2, Clock, Truck, Loader2, X, Save, Trash2,
  Inbox, XCircle,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const ORANGE = 'oklch(0.66 0.19 42)';
const CARD_BG = '#FFFFFF';
const HEADER_BG = '#F0F2F5';
const ROW_HOVER = 'rgba(26,35,55,0.04)';

type OrderStatus = 'new' | 'quoted' | 'lost' | 'pending' | 'scheduling' | 'raw_material' | 'vulcanizing' | 'qc_passed' | 'packing' | 'shipped' | 'completed';
type PaymentStatus = 'unpaid' | 'deposit_paid' | 'fully_paid';

interface OrderItem {
  id?: string;
  material_code: string;
  specifications: string;
  quantity_kg: string;
  unit_price: string;
  total_amount?: number;
}

interface Order {
  id: string;
  inquiry_id: string | null;
  order_no: string;
  total_amount: number | null;
  order_status: OrderStatus;
  payment_status: PaymentStatus;
  notes: string | null;
  created_at: string;
  updated_at: string;
  items?: OrderItem[];
}

// QuoteLine：报价抽屉中保存的完整多行明细结构
interface QuoteLine {
  material: string;
  spec: string;
  qty: string;
  unit: string;
}

interface Inquiry {
  id: string;
  name: string;
  company: string | null;
  material: string | null;
  quoted_price: number | null;
  quote_lines: QuoteLine[] | null;
  status: string | null;
}

const ORDER_STATUS_CFG: Record<OrderStatus, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  new:          { label: '新线索',   color: 'text-blue-600',   bg: 'bg-blue-100 border-blue-300',     icon: Inbox },
  quoted:       { label: '已报价',   color: 'text-purple-600', bg: 'bg-purple-100 border-purple-300', icon: FileText },
  lost:         { label: '已流失',   color: 'text-gray-500',   bg: 'bg-gray-100 border-gray-300',     icon: XCircle },
  pending:      { label: '待生产',   color: 'text-yellow-700', bg: 'bg-yellow-100 border-yellow-300', icon: Clock },
  scheduling:   { label: '排产中',   color: 'text-indigo-600', bg: 'bg-indigo-100 border-indigo-300', icon: Package2 },
  raw_material: { label: '原料备料', color: 'text-yellow-700', bg: 'bg-yellow-100 border-yellow-300', icon: Package2 },
  vulcanizing:  { label: '硫化/加工',color: 'text-orange-600', bg: 'bg-orange-100 border-orange-300', icon: Package2 },
  qc_passed:    { label: '质检完成', color: 'text-cyan-700',   bg: 'bg-cyan-100 border-cyan-300',     icon: CheckCircle2 },
  packing:      { label: '包装待发', color: 'text-amber-700',  bg: 'bg-amber-100 border-amber-300',   icon: Package2 },
  shipped:      { label: '已发货',   color: 'text-green-600',  bg: 'bg-green-100 border-green-300',   icon: Truck },
  completed:    { label: '已完成',   color: 'text-green-800',  bg: 'bg-green-200 border-green-400',   icon: CheckCircle2 },
};

const PAYMENT_STATUS_CFG: Record<PaymentStatus, { label: string; color: string }> = {
  unpaid:       { label: '未付款',    color: 'text-red-400' },
  deposit_paid: { label: '预付款已付', color: 'text-yellow-400' },
  fully_paid:   { label: '尾款已清',  color: 'text-green-400' },
};

const MATERIALS = ['FKM氟橡胶', 'EPDM三元乙丙', 'NBR丁腈橡胶', 'NR天然橡胶', 'CR氯丁橡胶', 'ESD防静电橡胶'];

// ── 工具函数 ──────────────────────────────────────────

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3600000);
  if (h < 1) return `${Math.floor(diff / 60000)} 分钟前`;
  if (h < 24) return `${h} 小时前`;
  return `${Math.floor(h / 24)} 天前`;
}

function fmt(n: number | null | undefined) {
  if (!n) return '—';
  return `¥${n.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`;
}

/**
 * 自动生成订单编号：pp-YYYYMMDD-NNN-RRRR
 * 查询今日已创建的订单数，序号 = count + 1，三位补零
 * 末尾随机4位防并发重复
 */
async function generateOrderNo(): Promise<string> {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm   = String(today.getMonth() + 1).padStart(2, '0');
  const dd   = String(today.getDate()).padStart(2, '0');
  const dateStr = `${yyyy}${mm}${dd}`;

  const startOfDay = `${yyyy}-${mm}-${dd}T00:00:00`;
  const endOfDay   = `${yyyy}-${mm}-${dd}T23:59:59`;

  const { count } = await supabase
    .from('orders')
    .select('id', { count: 'exact', head: true })
    .gte('created_at', startOfDay)
    .lte('created_at', endOfDay);

  const seq  = String((count ?? 0) + 1).padStart(3, '0');
  const rand = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  return `pp-${dateStr}-${seq}-${rand}`;
}

// ── 新建订单弹窗 ────────────────────────────────────────

const EMPTY_ITEM = (): OrderItem => ({ material_code: '', specifications: '', quantity_kg: '', unit_price: '' });

function NewOrderModal({ inquiries, onClose, onSaved, defaultInquiryId = '' }: {
  inquiries: Inquiry[];
  onClose: () => void;
  onSaved: () => void;
  defaultInquiryId?: string;
}) {
  const [inquiryId, setInquiryId]   = useState(defaultInquiryId);
  const [orderNo, setOrderNo]       = useState('');
  const [notes, setNotes]           = useState('');
  const [items, setItems]           = useState<OrderItem[]>([EMPTY_ITEM()]);
  const [saving, setSaving]         = useState(false);
  const [genLoading, setGenLoading] = useState(true);
  const [err, setErr]               = useState('');

  const IS = {
    background: '#FFFFFF',
    border: '1px solid #D0D5E2',
    color: '#1A2337',
    borderRadius: '4px',
    padding: '7px 10px',
    fontSize: '12px',
    outline: 'none',
    width: '100%',
  };

  // 打开弹窗时自动生成单号，并预填线索数据
  useEffect(() => {
    generateOrderNo().then(no => { setOrderNo(no); setGenLoading(false); });
    if (defaultInquiryId) {
      handleInquiryChange(defaultInquiryId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * 关联线索时，从 quote_lines 完整还原所有明细行。
   * quote_lines 是报价抽屉中保存的 JSON 数组，每项含 material/spec/qty/unit。
   * 若线索没有 quote_lines，则降级用 material 文本 + quoted_price 总价填一行。
   */
  const handleInquiryChange = (id: string) => {
    setInquiryId(id);
    if (!id) {
      setItems([EMPTY_ITEM()]);
      return;
    }
    const inq = inquiries.find(i => i.id === id);
    if (!inq) return;

    if (inq.quote_lines && Array.isArray(inq.quote_lines) && inq.quote_lines.length > 0) {
      // 优先路径：完整还原 quote_lines 中每一行
      const converted: OrderItem[] = inq.quote_lines.map((ql: QuoteLine) => ({
        material_code:  ql.material ?? '',
        specifications: ql.spec     ?? '',
        quantity_kg:    ql.qty      ?? '',
        unit_price:     ql.unit     ?? '',
      }));
      setItems(converted);
    } else {
      // 降级路径：仅有 material 文本 + quoted_price 总价
      setItems([{
        material_code:  inq.material ?? '',
        specifications: '',
        quantity_kg:    '',
        unit_price:     inq.quoted_price ? String(inq.quoted_price) : '',
      }]);
    }
  };

  const updateItem  = (idx: number, key: keyof OrderItem, val: string) =>
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [key]: val } : it));

  const addItem    = () => setItems(prev => [...prev, EMPTY_ITEM()]);
  const removeItem = (idx: number) => setItems(prev => prev.filter((_, i) => i !== idx));

  const lineTotal = (item: OrderItem) => {
    const qty   = parseFloat(item.quantity_kg);
    const price = parseFloat(item.unit_price);
    return isNaN(qty) || isNaN(price) ? null : qty * price;
  };

  const grandTotal = items.reduce((s, it) => s + (lineTotal(it) ?? 0), 0);

  const handleSave = async () => {
    setErr('');
    if (!orderNo.trim()) { setErr('订单编号不能为空'); return; }
    const validItems = items.filter(it => it.material_code);
    if (validItems.length === 0) { setErr('请至少填写一条明细（材料不能为空）'); return; }

    setSaving(true);

    // Step 1：写入 orders 主记录
    const { data: orderData, error: orderErr } = await supabase
      .from('orders')
      .insert([{
        inquiry_id:   inquiryId || null,
        order_no:     orderNo.trim(),
        total_amount: grandTotal > 0 ? grandTotal : null,
        notes:        notes.trim() || null,
      }])
      .select('id')
      .single();

    if (orderErr || !orderData) {
      setErr(orderErr?.message ?? '创建订单失败');
      setSaving(false);
      return;
    }

    const orderId = orderData.id;

    // Step 2：写入 order_items 明细行
    // 注意：total_amount 是数据库计算列（quantity_kg × unit_price），前端不传，由数据库自动计算
    const itemRows = validItems.map(it => ({
      order_id:       orderId,
      material_code:  it.material_code,
      specifications: it.specifications.trim() || null,
      quantity_kg:    parseFloat(it.quantity_kg) || null,
      unit_price:     parseFloat(it.unit_price)  || null,
    }));

    const { error: itemErr } = await supabase.from('order_items').insert(itemRows);
    if (itemErr) {
      setErr(`明细写入失败：${itemErr.message}`);
      setSaving(false);
      return;
    }

    // Step 3：若关联了线索，将线索状态更新为 pending（待生产），防止重复转单
    if (inquiryId) {
      await supabase
        .from('inquiries')
        .update({ status: 'pending' })
        .eq('id', inquiryId);
    }
    setSaving(false);
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-2xl rounded-sm border border-border p-6 space-y-5 overflow-y-auto"
        style={{ background: '#FFFFFF', maxHeight: '90vh' }}
      >
        {/* 标题 */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold" style={{ color: '#1A2337' }}>确认转单</h3>
            <p className="text-[11px] mt-0.5" style={{ color: '#5A6780' }}>关联线索数据将自动带入完整报价明细</p>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" style={{ color: '#5A6780' }} /></button>
        </div>

        {/* 基础信息 */}
        <div className="grid grid-cols-2 gap-3">
          {/* 关联线索 */}
          <div className="col-span-2">
            <label className="block text-xs mb-1.5" style={{ color: '#5A6780' }}>
              关联线索
              <span className="ml-1.5 opacity-60">（自动填充完整报价明细，无线索可不选）</span>
            </label>
            <select value={inquiryId} onChange={e => handleInquiryChange(e.target.value)} style={IS}>
              <option value="">— 不关联线索 —</option>
              {inquiries.filter(i => i.status !== 'lost').map(i => (
                <option key={i.id} value={i.id}>
                  {i.name}{i.company ? ` · ${i.company}` : ''}{i.material ? ` — ${i.material}` : ''}
                  {i.quoted_price ? ` （¥${i.quoted_price}）` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* 订单编号 */}
          <div>
            <label className="block text-xs mb-1.5" style={{ color: '#5A6780' }}>
              订单编号 *
              {genLoading && <span className="ml-1.5 text-[11px] opacity-60">生成中…</span>}
            </label>
            <input value={orderNo} onChange={e => setOrderNo(e.target.value)}
              style={{ ...IS, fontFamily: 'monospace', color: ORANGE }}
              placeholder={genLoading ? '自动生成中…' : ''}
            />
          </div>

          {/* 备注 */}
          <div>
            <label className="block text-xs mb-1.5" style={{ color: '#5A6780' }}>备注 / 交货期</label>
            <input value={notes} onChange={e => setNotes(e.target.value)} style={IS} placeholder="规格要求、客户特殊需求…" />
          </div>
        </div>

        {/* 明细行 */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-bold" style={{ color: '#5A6780' }}>产品明细 *（可多行）</label>
            <button onClick={addItem}
              className="flex items-center gap-1 text-xs px-2 py-1 rounded-sm border border-border hover:border-orange-400/40 transition-colors"
              style={{ color: '#5A6780' }}
            ><Plus className="w-3 h-3" />添加一行</button>
          </div>

          {/* 列头 */}
          <div className="grid gap-1.5 text-[10px] font-mono font-bold tracking-widest uppercase mb-1 px-1"
            style={{ color: '#5A6780', gridTemplateColumns: '2fr 2fr 1fr 1fr auto' }}>
            <span>材料</span><span>规格</span><span>数量 kg</span><span>单价 元/kg</span><span></span>
          </div>

          <div className="space-y-2">
            {items.map((item, idx) => {
              const lt = lineTotal(item);
              return (
                <div key={idx} className="grid gap-1.5 items-center" style={{ gridTemplateColumns: '2fr 2fr 1fr 1fr auto' }}>
                  <select value={item.material_code} onChange={e => updateItem(idx, 'material_code', e.target.value)} style={IS}>
                    <option value="">— 选择 —</option>
                    {MATERIALS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <input value={item.specifications} onChange={e => updateItem(idx, 'specifications', e.target.value)}
                    style={IS} placeholder="厚度/宽度等" />
                  <input type="number" value={item.quantity_kg} onChange={e => updateItem(idx, 'quantity_kg', e.target.value)}
                    style={IS} placeholder="0" />
                  <input type="number" value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', e.target.value)}
                    style={IS} placeholder="0.00" />
                  <button onClick={() => removeItem(idx)} disabled={items.length === 1}
                    className="p-1.5 rounded-sm border border-border hover:border-red-400/40 disabled:opacity-30 transition-colors"
                  ><Trash2 className="w-3 h-3 text-red-400" /></button>

                  {lt !== null && (
                    <div className="col-span-5 text-right text-[11px] font-mono pr-8" style={{ color: ORANGE }}>
                      小计：¥{lt.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* 总额预览 */}
        {grandTotal > 0 && (
          <div className="rounded-sm px-4 py-3 text-right text-sm font-black font-mono border border-orange-400/20"
            style={{ background: `${ORANGE}12`, color: ORANGE }}>
            订单总额：¥{grandTotal.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
          </div>
        )}

        {err && (
          <p className="text-xs text-red-400 flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />{err}
          </p>
        )}

        {/* 提交 */}
        <div className="flex gap-3 pt-1">
          <button onClick={handleSave} disabled={saving || genLoading}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-sm font-bold text-sm disabled:opacity-60"
            style={{ background: ORANGE, color: '#fff' }}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? '保存中…' : '保存订单'}
          </button>
          <button onClick={onClose} className="px-4 py-2.5 rounded-sm border border-border text-sm" style={{ color: '#5A6780' }}>
            取消
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ── 主视图 ──────────────────────────────────────────────

export function OrdersView({
  preselectedInquiryId = '',
  onPreselectedConsumed,
}: {
  preselectedInquiryId?: string;
  onPreselectedConsumed?: () => void;
}) {
  const [orders, setOrders]         = useState<Order[]>([]);
  const [inquiries, setInquiries]   = useState<Inquiry[]>([]);
  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch]         = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<Record<string, OrderItem[]>>({});
  const [showNew, setShowNew]       = useState(false);
  // 已有生产任务的订单 ID 集合，用于防止重复创建
  const [hasTaskIds, setHasTaskIds] = useState<Set<string>>(new Set());

  // 当收到预选线索 ID 时，自动打开新建订单弹窗
  useEffect(() => {
    if (preselectedInquiryId && inquiries.length > 0) {
      setShowNew(true);
      onPreselectedConsumed?.();
    }
  }, [preselectedInquiryId, inquiries]);

  const fetchAll = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true); else setLoading(true);

    const [ordersRes, inqRes] = await Promise.all([
      supabase.from('orders').select('*').order('created_at', { ascending: false }),
      // 拉取 quote_lines，供新建订单时完整还原多行明细；排除已流失线索
      supabase
        .from('inquiries')
        .select('id, name, company, material, quoted_price, quote_lines, status')
        .neq('status', 'lost')
        .order('created_at', { ascending: false }),
    ]);

    if (ordersRes.data) setOrders(ordersRes.data as Order[]);
    if (inqRes.data) {
      setInquiries(inqRes.data.map((i: Record<string, unknown>) => ({
        id:           i.id          as string,
        name:         i.name        as string,
        company:      i.company     as string | null,
        material:     i.material    as string | null,
        quoted_price: i.quoted_price as number | null,
        quote_lines:  i.quote_lines  as QuoteLine[] | null,
        status:       i.status       as string | null,
      })));
    }
    setLoading(false); setRefreshing(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // 展开订单时懒加载明细 + 检查是否已有生产任务
  const handleExpand = async (orderId: string) => {
    if (expandedId === orderId) { setExpandedId(null); return; }
    setExpandedId(orderId);
    if (!expandedItems[orderId]) {
      const [itemsRes, taskRes] = await Promise.all([
        supabase.from('order_items').select('*').eq('order_id', orderId).order('id'),
        supabase.from('production_tasks').select('id', { count: 'exact', head: true }).eq('order_id', orderId),
      ]);
      setExpandedItems(prev => ({ ...prev, [orderId]: (itemsRes.data ?? []) as OrderItem[] }));
      if ((taskRes.count ?? 0) > 0) {
        setHasTaskIds(prev => new Set(prev).add(orderId));
      }
    }
  };

  const updateOrderStatus = async (id: string, field: 'order_status' | 'payment_status', value: string) => {
    await supabase.from('orders').update({ [field]: value }).eq('id', id);
    setOrders(prev => prev.map(o => o.id === id ? { ...o, [field]: value } : o));
  };

  const convertToProduction = async (order: Order) => {
    // 防重复：再次确认没有生产任务
    const { count } = await supabase
      .from('production_tasks')
      .select('id', { count: 'exact', head: true })
      .eq('order_id', order.id);
    if ((count ?? 0) > 0) {
      setHasTaskIds(prev => new Set(prev).add(order.id));
      return;
    }
    const { error } = await supabase.from('production_tasks').insert([{
      order_id:   order.id,
      stage:      'scheduling',
      tech_notes: order.notes,
    }]);
    if (!error) {
      setHasTaskIds(prev => new Set(prev).add(order.id));
      await supabase.from('orders').update({ order_status: 'scheduling' }).eq('id', order.id);
      // 同步线索状态
      if (order.inquiry_id) {
        await supabase.from('inquiries').update({ status: 'scheduling' }).eq('id', order.inquiry_id);
      }
      fetchAll(true);
    }
  };

  const filtered = orders.filter(o =>
    !search || [o.order_no, o.notes ?? ''].some(v => v.toLowerCase().includes(search.toLowerCase()))
  );

  const totalAmount = orders.reduce((s, o) => s + (o.total_amount ?? 0), 0);
  const thisMonth   = orders
    .filter(o => new Date(o.created_at).getMonth() === new Date().getMonth())
    .reduce((s, o) => s + (o.total_amount ?? 0), 0);

  return (
    <div className="space-y-6">
      {/* 摘要卡 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: '总订单数',  value: orders.length,                                           color: '#1A2337' },
          { label: '本月订单额', value: `¥${(thisMonth / 10000).toFixed(1)}万`,                  color: ORANGE },
          { label: '累计总额',  value: `¥${(totalAmount / 10000).toFixed(1)}万`,                 color: '#1A2337' },
          { label: '待生产',    value: orders.filter(o => o.order_status === 'pending').length, color: '#D97706' },
        ].map(({ label, value, color }) => (
          <div key={label} className="rounded-sm border border-border p-4" style={{ background: CARD_BG }}>
            <div className="text-xs font-mono mb-2" style={{ color: '#5A6780' }}>{label}</div>
            <div className="text-2xl font-black" style={{ color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* 工具栏 */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: '#5A6780' }} />
          <input type="search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="搜索订单编号、备注…"
            className="w-full pl-9 pr-4 py-2 rounded-sm text-sm outline-none"
            style={{ background: '#FFFFFF', border: '1px solid #D0D5E2', color: '#1A2337' }}
          />
        </div>
        <button onClick={() => fetchAll(true)} disabled={refreshing}
          className="p-2 rounded-sm border border-border disabled:opacity-50"
        ><RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} style={{ color: '#5A6780' }} /></button>
      </div>

      {/* 表格 */}
      {loading ? (
        <div className="p-16 flex justify-center"><RefreshCw className="w-8 h-8 animate-spin" style={{ color: ORANGE }} /></div>
      ) : filtered.length === 0 ? (
        <div className="p-16 flex flex-col items-center gap-3 rounded-sm border border-border" style={{ background: CARD_BG, color: '#5A6780' }}>
          <AlertCircle className="w-10 h-10" /><span>暂无订单数据，请先从线索看板转单</span>
        </div>
      ) : (
        <div className="rounded-sm border border-border overflow-hidden" style={{ background: CARD_BG }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border" style={{ background: HEADER_BG }}>
                  {['订单编号', '关联线索', '订单总额', '订单状态', '付款状态', '创建时间', ''].map(h => (
                    <th key={h} className="text-left text-xs font-mono font-bold tracking-widest uppercase px-4 py-3 whitespace-nowrap" style={{ color: ORANGE }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {filtered.map(order => {
                    const osCfg  = ORDER_STATUS_CFG[order.order_status];
                    const OsIcon = osCfg.icon;
                    const linkedInq = inquiries.find(i => i.id === order.inquiry_id);
                    const detailItems = expandedItems[order.id] ?? [];
                    return (
                      <>
                        <motion.tr key={order.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                          onClick={() => handleExpand(order.id)}
                          className="border-b border-border cursor-pointer transition-colors"
                          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = ROW_HOVER; }}
                          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                        >
                          <td className="px-4 py-3 text-xs font-mono font-bold" style={{ color: ORANGE }}>{order.order_no}</td>
                          <td className="px-4 py-3 text-xs" style={{ color: '#5A6780' }}>
                            {linkedInq
                              ? <span style={{ color: '#1A2337' }}>{linkedInq.name}{linkedInq.company ? ` · ${linkedInq.company}` : ''}</span>
                              : <span style={{ color: '#9CA3AF' }}>—</span>}
                          </td>
                          <td className="px-4 py-3 text-sm font-bold font-mono" style={{ color: '#1A2337' }}>
                            {fmt(order.total_amount)}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-sm border ${osCfg.bg} ${osCfg.color}`}>
                              <OsIcon className="w-3 h-3" />{osCfg.label}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-medium ${PAYMENT_STATUS_CFG[order.payment_status].color}`}>
                              {PAYMENT_STATUS_CFG[order.payment_status].label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs" style={{ color: '#5A6780' }}>{timeAgo(order.created_at)}</td>
                          <td className="px-4 py-3" style={{ color: '#5A6780' }}>
                            {expandedId === order.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </td>
                        </motion.tr>

                        {expandedId === order.id && (
                          <motion.tr key={`${order.id}-detail`} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                            <td colSpan={7} className="px-6 py-5 border-b border-border" style={{ background: '#F7F8FA' }}>
                              <div className="space-y-5">

                                {/* 明细行 */}
                                <div>
                                  <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: '#5A6780' }}>产品明细</p>
                                  {detailItems.length === 0 ? (
                                    <p className="text-xs" style={{ color: '#5A6780' }}>加载中…</p>
                                  ) : (
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr style={{ color: '#5A6780' }}>
                                          {['材料', '规格', '数量(kg)', '单价(元/kg)', '小计'].map(h => (
                                            <th key={h} className="text-left font-mono pb-1 pr-4">{h}</th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {detailItems.map((it, i) => (
                                            <tr key={i} className="border-t border-border">
                                              <td className="py-1.5 pr-4 font-medium" style={{ color: '#1A2337' }}>{it.material_code || '—'}</td>
                                              <td className="py-1.5 pr-4" style={{ color: '#5A6780' }}>{it.specifications || '—'}</td>
                                              <td className="py-1.5 pr-4 font-mono" style={{ color: '#1A2337' }}>{it.quantity_kg ?? '—'}</td>
                                              <td className="py-1.5 pr-4 font-mono" style={{ color: '#1A2337' }}>{it.unit_price ? `¥${it.unit_price}` : '—'}</td>
                                            <td className="py-1.5 font-mono font-bold" style={{ color: ORANGE }}>
                                              {it.total_amount != null
                                                ? fmt(it.total_amount)
                                                : (it.quantity_kg != null && it.unit_price != null)
                                                  ? fmt(Number(it.quantity_kg) * Number(it.unit_price))
                                                  : '—'}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}
                                </div>

                                {/* 操作区 */}
                                <div className="grid md:grid-cols-3 gap-4 pt-3 border-t border-border">
                                  <div>
                                    <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: '#5A6780' }}>更新订单状态</p>
                                    <select value={order.order_status}
                                      onChange={e => updateOrderStatus(order.id, 'order_status', e.target.value)}
                                      className="w-full text-xs rounded-sm px-3 py-2 outline-none"
                                      style={{ background: '#FFFFFF', border: '1px solid #D0D5E2', color: '#1A2337' }}
                                    >
                                      {Object.entries(ORDER_STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                    </select>
                                  </div>
                                  <div>
                                    <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: '#5A6780' }}>更新付款状态</p>
                                    <select value={order.payment_status}
                                      onChange={e => updateOrderStatus(order.id, 'payment_status', e.target.value)}
                                      className="w-full text-xs rounded-sm px-3 py-2 outline-none"
                                      style={{ background: '#FFFFFF', border: '1px solid #D0D5E2', color: '#1A2337' }}
                                    >
                                      {Object.entries(PAYMENT_STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                    </select>
                                  </div>
                                  <div>
                                    <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: '#5A6780' }}>操作</p>
                                    {(() => {
                                      const alreadyHasTask = hasTaskIds.has(order.id);
                                      return alreadyHasTask ? (
                                        <button disabled
                                          className="flex items-center gap-2 text-xs font-bold px-4 py-2 rounded-sm w-full justify-center"
                                          style={{ background: '#E5E7EB', color: '#9CA3AF', cursor: 'not-allowed' }}
                                          title="该订单已有生产任务，不可重复创建"
                                        ><CheckCircle2 className="w-3.5 h-3.5" />已创建生产任务</button>
                                      ) : order.order_status === 'pending' ? (
                                        <button onClick={() => convertToProduction(order)}
                                          className="flex items-center gap-2 text-xs font-bold px-4 py-2 rounded-sm w-full justify-center"
                                          style={{ background: ORANGE, color: '#fff' }}
                                        ><FileText className="w-3.5 h-3.5" />转化为生产任务</button>
                                      ) : null;
                                    })()}
                                    {order.notes && <p className="text-xs mt-2" style={{ color: '#5A6780' }}>备注：{order.notes}</p>}
                                  </div>
                                </div>

                              </div>
                            </td>
                          </motion.tr>
                        )}
                      </>
                    );
                  })}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-border text-xs" style={{ background: HEADER_BG, color: '#5A6780' }}>
            共 <span className="font-bold" style={{ color: '#1A2337' }}>{filtered.length}</span> 条订单 · 点击行展开明细与操作
          </div>
        </div>
      )}

      {showNew && (
        <NewOrderModal
          inquiries={inquiries}
          defaultInquiryId={preselectedInquiryId}
          onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); fetchAll(true); setExpandedItems({}); }}
        />
      )}
    </div>
  );
}
